import { client } from "@scayla/api-client-fetch/client";
import { onScopeDispose, watch } from "vue";
import { useRouter } from "vue-router";

import { isTenantNotFoundError } from "@/domains/tenant/helpers/tenantNotFound";
import { $t } from "@/i18n";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";

/**
 * Recovers a tab whose current tenant was deleted elsewhere (another tab or user). A TenantNotFoundError
 * gets a readable message and reloads the tenant list; it does not redirect by itself, because the
 * Mandators pages also get it for other tenants. If the current tenant is really gone, the workspace
 * falls back to another one and reports it in removedTenantId, and this opens that tenant's Home.
 */
export function useTenantNotFoundRecovery() {
  const workspace = useWorkspace();
  const router = useRouter();
  let refetching: Promise<unknown> | undefined;

  const interceptorId = client.interceptors.error.use((error) => {
    if (!isTenantNotFoundError(error)) return error;
    refetching ??= workspace.tenantsRefetch().finally(() => (refetching = undefined));
    return { ...error, message: $t("tenantManagement.message.tenantUnavailable") };
  });

  watch(
    () => workspace.removedTenantId,
    (removedTenantId) => {
      if (!removedTenantId || !workspace.tenantId) return;
      void router.push({ name: Routes.HOME, query: { tenant: workspace.tenantId } });
    },
  );

  onScopeDispose(() => client.interceptors.error.eject(interceptorId));
}
